import { useState } from 'react';
import { Button } from './ui/button';
import { usePWA } from '../hooks/usePWA';
import { Download, Check, Smartphone, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function InstallPWABanner({ language = 'pl', galleryName = '' }) {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [dismissed, setDismissed] = useState(false);
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    await installApp();
    setInstalling(false);
  };

  // Don't show if already installed, not installable, or dismissed
  if (isInstalled || !isInstallable || dismissed) {
    return null;
  }

  const texts = {
    pl: {
      title: 'Zainstaluj galerię',
      description: galleryName 
        ? `Dodaj "${galleryName}" do ekranu głównego`
        : 'Dodaj galerię do ekranu głównego',
      benefits: 'Szybki dostęp, działa offline',
      button: 'Zainstaluj',
      installing: 'Instalowanie...',
    },
    en: {
      title: 'Install Gallery',
      description: galleryName
        ? `Add "${galleryName}" to home screen`
        : 'Add gallery to home screen',
      benefits: 'Quick access, works offline',
      button: 'Install',
      installing: 'Installing...',
    }
  };

  const t = texts[language] || texts.pl;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50"
      >
        <div className="bg-card border border-border rounded-lg shadow-2xl p-4">
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
          
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 rounded-lg p-2">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm">{t.title}</h3>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {t.description}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                {t.benefits}
              </p>
            </div>
          </div>
          
          <Button
            onClick={handleInstall}
            disabled={installing}
            className="w-full mt-3"
            size="sm"
          >
            {installing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {t.installing}
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                {t.button}
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// Compact version for inline use
export function InstallPWAButton({ language = 'pl', variant = 'outline', size = 'sm' }) {
  const { isInstallable, isInstalled, installApp } = usePWA();
  const [installing, setInstalling] = useState(false);

  const handleInstall = async () => {
    setInstalling(true);
    await installApp();
    setInstalling(false);
  };

  if (isInstalled) {
    return (
      <Button variant="ghost" size={size} disabled>
        <Check className="w-4 h-4 mr-2" />
        {language === 'pl' ? 'Zainstalowano' : 'Installed'}
      </Button>
    );
  }

  if (!isInstallable) {
    return null;
  }

  return (
    <Button
      onClick={handleInstall}
      disabled={installing}
      variant={variant}
      size={size}
    >
      {installing ? (
        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
      ) : (
        <Download className="w-4 h-4 mr-2" />
      )}
      {language === 'pl' ? 'Zainstaluj' : 'Install'}
    </Button>
  );
}